import * as React from 'react'

import { Box, Radio, Section } from '@factorialco/deprecated-design-system'
import { xor } from 'lodash'

import { observer } from 'mobx-react'

import UserSelector from 'modules/core/components/UserSelector'
import { useAsyncData } from 'modules/core/hooks/useAsyncData'
import { useLazyRef } from 'modules/core/hooks/useLazyRef'
import asyncAction from 'modules/core/lib/asyncAction'
import i18n from 'modules/core/lib/i18n'
import users from 'modules/core/store/collections/users'

import type { ContractVersions } from 'modules/contracts/store/collections/contractVersions'
import { ResponsivePage } from 'modules/layout/components/ResponsivePage'
import { PermissionGroupsLayout } from 'modules/permissions/components/PermissionGroupsLayout'
import FqlGroupSelection from 'modules/permissions/components/Permissions/Item/Strategies/FqlGroupSelector'
import { VisibilityForm } from 'modules/permissions/pages/settings/VisibilityPage/VisibilitySection/VisibilityForm'
import permissionAccessGroups from 'modules/permissions/store/collections/permissionAccessGroups'
import PermissionAccessGroup from 'modules/permissions/store/models/PermissionAccessGroup'
import type PermissionGroup from 'modules/permissions/store/models/PermissionGroup'
import LegalEntityMultiselector from 'modules/timeoff/components/LegalEntityMultiselector'
import LocationMultiselector from 'modules/timeoff/components/LocationMultiselector'
import TeamMultiselector from 'modules/timeoff/components/TeamMultiselector'

interface Props {
  permissionGroup: PermissionGroup
  contractVersions: ContractVersions
  externalLoading: boolean
}

function VisibilitySection({ permissionGroup, contractVersions, externalLoading }: Props) {
  const form = useLazyRef(() => new VisibilityForm()).current
  const group = permissionGroup.permissionAccessGroups[0]
  const [loading, setLoading] = React.useState(false)
  const [selectedMembers, setMembers] = React.useState<number[]>([])

  // TODO: Pull this fetching up to avoid showing a spinner + layout shift on navigation between tabs
  useAsyncData(async () => {
    setLoading(true)
    await permissionAccessGroups.fetch()
    const group = permissionGroup.permissionAccessGroups[0]
    form.get('strategy').set(group?.attributes.strategy)
    form.get('groupable_type').set(group?.attributes.groupable_type ?? '')
    form.get('groupable_id').set(group?.attributes.groupable_id?.toString() ?? '')
    form.get('query_id').set(group?.attributes.query_id ?? '')
    form.get('team_ids').set(group?.teamIds ?? [])
    form.get('location_ids').set(group?.locationIds ?? [])
    form.get('legal_entity_ids').set(group?.legalEntityIds ?? [])
    form.cleanAll()
    setMembers(group?.attributes.members ?? [])
    setLoading(false)
  }, [])

  const setMember = (id: number | null | undefined) =>
    id ? setMembers(xor(selectedMembers, [id])) : setMembers([])

  async function saveChanges() {
    setLoading(true)

    const strategy = () => {
      if (form.isFqlMultiselect) return 'fql'
      else return form.get('strategy').value
    }
    const payload = {
      strategy: strategy(),
      members: selectedMembers,
      permissions_group_id: permissionGroup.attributes.id,
      groupable_type: null,
      groupable_id: null,
      query_id: '',
      team_ids: [],
      location_ids: [],
      legal_entity_ids: [],
    }

    if (form.isFql) {
      payload.query_id = form.get('query_id').value
    }

    if (form.isFqlMultiselect) {
      payload.team_ids = form.get('team_ids').value
      payload.location_ids = form.get('location_ids').value
      payload.legal_entity_ids = form.get('legal_entity_ids').value
    }

    await asyncAction(async () => {
      const group = permissionGroup.permissionAccessGroups[0]
      const response = await group?.save(payload)
      form.get('strategy').set(response.strategy)
      form.get('groupable_type').set(response.groupable_type ?? '')
      form.get('groupable_id').set(response.groupable_id?.toString() ?? '')
      form.get('query_id').set(response.query_id ?? '')
      form.get('team_ids').set(response.team_ids ?? [])
      form.get('location_ids').set(response.location_ids ?? [])
      form.get('legal_entity_ids').set(response.legal_entity_ids ?? [])
      form.cleanAll()
    })

    setLoading(false)
  }

  const emptyFields =
    !form.get('team_ids').value.length &&
    !form.get('location_ids').value.length &&
    !form.get('legal_entity_ids').value.length &&
    !form.get('query_id').value &&
    !selectedMembers.length

  const membersUnchanged =
    selectedMembers.length == group?.attributes.members.length &&
    selectedMembers.every((item) => group?.attributes.members.includes(item))

  return (
    <ResponsivePage.Body showHeader={false} loading={loading || externalLoading} error={null}>
      <PermissionGroupsLayout />
      <Section
        illustration="group"
        title={i18n.t('settings.permissions.visibility.title')}
        description={i18n.t('settings.permissions.visibility.description')}
        action={{
          label: i18n.t('common.save_changes'),
          onClick: saveChanges,
          disabled: emptyFields || (!form.isDirty && membersUnchanged),
        }}
      >
        <Radio.Wrap>
          <Box gap="s16">
            <Radio
              name="everyone"
              option={{
                label: i18n.t('settings.permissions.visibility.choices.everyone'),
                value: PermissionAccessGroup.Strategies.everyone,
              }}
              description={i18n.t('settings.permissions.visibility.choices.everyone_desc')}
              value={form.get('strategy').value}
              onChange={(value) => form.get('strategy').set(value)}
            />
            <Radio
              name="dynamic_group"
              option={{
                label: i18n.t('settings.permissions.visibility.choices.dynamic_group'),
                value: PermissionAccessGroup.Strategies.fqlMultiselect,
              }}
              description={i18n.t('settings.permissions.visibility.choices.dynamic_group_desc')}
              value={form.get('strategy').value}
              onChange={(value) => form.get('strategy').set(value)}
            >
              <Box gap="s12">
                <TeamMultiselector teamIds={group?.attributes.team_ids} form={form} />
                <LocationMultiselector locationIds={group?.attributes.location_ids} form={form} />
                <LegalEntityMultiselector parent={group} form={form} />
              </Box>
            </Radio>
            <Radio
              name="fql_group"
              option={{
                label: i18n.t('settings.permissions.visibility.choices.relative_selection'),
                value: PermissionAccessGroup.Strategies.fql,
              }}
              description={i18n.t(
                'settings.permissions.visibility.choices.relative_selection_desc'
              )}
              value={form.get('strategy').value}
              onChange={(value) => form.get('strategy').set(value)}
            >
              <FqlGroupSelection fqlQuery={form.get('query_id')} />
            </Radio>
            <Radio
              name="manual_selection"
              option={{
                label: i18n.t('settings.permissions.visibility.choices.manual_selection'),
                value: PermissionAccessGroup.Strategies.members,
              }}
              description={i18n.t('settings.permissions.visibility.choices.manual_selection_desc')}
              value={form.get('strategy').value}
              onChange={(value) => form.get('strategy').set(value)}
            >
              <UserSelector
                onSelect={setMember}
                onSelectMultiple={setMembers}
                selectedIds={selectedMembers}
                users={users.models}
                contractVersions={contractVersions}
              />
            </Radio>
          </Box>
        </Radio.Wrap>
      </Section>
    </ResponsivePage.Body>
  )
}

export default observer(VisibilitySection)
